/**
 * Zod Schemas for Special Showcases API
 * /api/recommendations/special-showcases endpoint schemas
 *
 * Returns special showcase types: recently_viewed, trending, new_arrivals, best_deals, your_wishlist, recent_searches, holiday, similar_to_liked, similar_to_purchased
 */

import { z } from 'zod';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

export const SpecialShowcaseTypeEnum = z.enum([
  'recently_viewed',
  'trending',
  'new_arrivals',
  'best_deals',
  'your_wishlist',
  'recent_searches',
  'holiday',
  'similar_to_liked',
  'similar_to_purchased',
  'near_you',
]);

export type SpecialShowcaseType = z.infer<typeof SpecialShowcaseTypeEnum>;

export const SpecialShowcasesQuerySchema = z.object({
  type: z.enum(['user', 'session']),
  userId: z.string().optional().transform((val) => val ? parseInt(val, 10) : undefined),
  sessionId: z.string().optional(),
  anonymousId: z.string().optional(),
  days: z.string().optional().default('30').transform((val) => parseInt(val, 10)),
  showcaseTypes: z.string().optional().default('recently_viewed,trending,new_arrivals,best_deals,your_wishlist,recent_searches,holiday,similar_to_liked,similar_to_purchased,near_you'),
  limit: z.string().optional().default('12').transform((val) => parseInt(val, 10)),
});

export type SpecialShowcasesQuery = z.infer<typeof SpecialShowcasesQuerySchema>;

// =============================================================================
// SHOWCASE SCHEMAS
// =============================================================================

export const SpecialShowcaseSchema = z.object({
  type: SpecialShowcaseTypeEnum,
  title: z.string(),
  listings: z.array(z.any()),
  sourceListingId: z.number().optional(),
});

export type SpecialShowcase = z.infer<typeof SpecialShowcaseSchema>;

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

export const SpecialShowcasesSuccessResponseSchema = z.object({
  success: z.literal(true),
  showcases: z.array(SpecialShowcaseSchema),
});

export type SpecialShowcasesSuccessResponse = z.infer<typeof SpecialShowcasesSuccessResponseSchema>;

export const SpecialShowcasesErrorResponseSchema = z.object({
  success: z.literal(false),
  showcases: z.array(z.any()).default([]),
  error: z.string(),
});

export type SpecialShowcasesErrorResponse = z.infer<typeof SpecialShowcasesErrorResponseSchema>;

export type SpecialShowcasesResponse = SpecialShowcasesSuccessResponse | SpecialShowcasesErrorResponse;
